import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Clock, MapPin, Phone, ChevronRight, Gift, CalendarDays } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCATIONS = {
  grosseto: {
    label: 'Grosseto',
    venue: 'Caribbean Blue Moon Dance School',
    address: 'Via Ambra 19, Grosseto',
    period: 'Dal 21 Settembre al 1 Ottobre 2026',
    disciplines: [
      {
        id: 'trial-danza-moderna', name: 'Danza Moderna', isNew: true, color: '#FF7043',
        slots: [
          { day: 'MAR', date: '22/09', time: '18:00' },
          { day: 'VEN', date: '25/09', time: '19:00' },
        ],
      },
      {
        id: 'trial-heels', name: 'Heels', isNew: true, color: '#E91E63',
        slots: [
          { day: 'MAR', date: '22/09', time: '19:30' },
          { day: 'VEN', date: '25/09', time: '20:00' },
          { day: 'MAR', date: '29/09', time: '19:30' },
        ],
      },
      {
        id: 'trial-tango-argentino', name: 'Tango Argentino', color: '#8E24AA',
        slots: [
          { day: 'MER', date: '23/09', time: '20:30' },
          { day: 'MAR', date: '29/09', time: '20:30' },
        ],
      },
      {
        id: 'trial-balli-di-gruppo', name: 'Balli di Gruppo', color: '#0288D1',
        slots: [
          { day: 'LUN', date: '21/09', time: '11:00' },
          { day: 'MER', date: '23/09', time: '11:00' },
          { day: 'LUN', date: '28/09', time: '11:00' },
          { day: 'MER', date: '30/09', time: '19:30' },
        ],
      },
      {
        id: 'trial-pilates', name: 'Pilates', isNew: true, color: '#00897B',
        slots: [
          { day: 'MAR', date: '22/09', time: '9:30' },
          { day: 'GIO', date: '24/09', time: '9:30' },
        ],
      },
      {
        id: 'trial-break-dance', name: 'Break Dance', color: '#F4511E',
        slots: [
          { day: 'GIO', date: '24/09', time: '18:30' },
          { day: 'LUN', date: '28/09', time: '18:30' },
        ],
      },
      {
        id: 'trial-salsa-bachata-coppie', name: 'Salsa & Bachata Coppie', color: '#FF7043',
        slots: [
          { day: 'LUN', date: '21/09', time: '21:30' },
          { day: 'GIO', date: '24/09', time: '20:30' },
          { day: 'LUN', date: '28/09', time: '20:30' },
          { day: 'LUN', date: '28/09', time: '21:30' },
          { day: 'MER', date: '30/09', time: '20:30' },
          { day: 'MER', date: '30/09', time: '21:30' },
        ],
      },
      {
        id: 'trial-boogie-woogie', name: 'Boogie Woogie', isNew: true, color: '#FBC02D',
        slots: [
          { day: 'MAR', date: '22/09', time: '20:30' },
          { day: 'MAR', date: '29/09', time: '21:30' },
        ],
      },
      {
        id: 'trial-hip-hop-latino', name: 'Hip-Hop Latino Americano', color: '#0288D1',
        slots: [
          { day: 'LUN', date: '21/09', time: '17:30', note: '6/10 anni' },
          { day: 'LUN', date: '21/09', time: '18:30', note: '11+ anni' },
          { day: 'MER', date: '23/09', time: '17:30', note: '6/10 anni' },
          { day: 'MER', date: '23/09', time: '18:30', note: '11+ anni' },
          { day: 'VEN', date: '25/09', time: '17:30', note: '6/10 anni' },
          { day: 'VEN', date: '25/09', time: '18:30', note: '11+ anni' },
          { day: 'MER', date: '30/09', time: '17:30', note: '6/10 anni' },
          { day: 'MER', date: '30/09', time: '18:30', note: '11+ anni' },
        ],
      },
      {
        id: 'trial-danza-classica-adulti', name: 'Danza Classica Adulti', isNew: true, color: '#8E24AA',
        slots: [
          { day: 'MAR', date: '22/09', time: '10:30' },
          { day: 'GIO', date: '24/09', time: '10:30' },
        ],
      },
      {
        id: 'trial-reggaeton', name: 'Reggaeton', color: '#F4511E',
        slots: [
          { day: 'LUN', date: '21/09', time: '19:30' },
          { day: 'GIO', date: '24/09', time: '19:30' },
          { day: 'LUN', date: '28/09', time: '19:30' },
        ],
      },
      {
        id: 'trial-salsa-in-linea', name: 'Salsa in Linea', color: '#00897B',
        slots: [
          { day: 'LUN', date: '21/09', time: '20:30' },
          { day: 'GIO', date: '24/09', time: '21:30' },
        ],
      },
      {
        id: 'trial-preparazione-atletica', name: 'Preparazione Atletica', isNew: true, color: '#546E7A',
        slots: [
          { day: 'MAR', date: '29/09', time: '18:30' },
        ],
      },
      {
        id: 'trial-baby-dance', name: 'Baby Dance', ageNote: '4/6 anni', color: '#FBC02D',
        slots: [
          { day: 'MAR', date: '22/09', time: '17:00' },
          { day: 'GIO', date: '24/09', time: '17:30' },
          { day: 'LUN', date: '28/09', time: '17:30' },
        ],
      },
      {
        id: 'trial-bachata-tradicional', name: 'Bachata Tradicional', isNew: true, color: '#E91E63',
        slots: [
          { day: 'MAR', date: '22/09', time: '21:30' },
          { day: 'GIO', date: '01/10', time: '21:30' },
        ],
      },
      {
        id: 'trial-disco-dance', name: 'Disco Dance', isNew: true, color: '#FF7043',
        slots: [
          { day: 'GIO', date: '24/09', time: '18:30' },
        ],
      },
    ],
  },
  follonica: {
    label: 'Follonica',
    venue: 'Palestra Altroquando',
    address: "Via dell'Edilizia 108, Follonica",
    period: 'Mercoledì 23 e Venerdì 25 Settembre 2026',
    disciplines: [
      {
        id: 'trial-fo-baby-dance', name: 'Baby Dance', ageNote: '4/6 anni', color: '#FBC02D',
        slots: [
          { day: 'MER', date: '23/09', time: '16:30' },
          { day: 'VEN', date: '25/09', time: '16:30' },
        ],
      },
      {
        id: 'trial-fo-hip-hop-latino', name: 'Hip-Hop, Latino Americano', color: '#0288D1',
        slots: [
          { day: 'MER', date: '23/09', time: '17:30', note: '6/10 anni' },
          { day: 'MER', date: '23/09', time: '18:30', note: '11+ anni' },
          { day: 'VEN', date: '25/09', time: '17:30', note: '6/10 anni' },
          { day: 'VEN', date: '25/09', time: '18:30', note: '11+ anni' },
        ],
      },
      {
        id: 'trial-fo-balli-di-gruppo', name: 'Balli di Gruppo', color: '#00897B',
        slots: [
          { day: 'MER', date: '23/09', time: '19:30' },
          { day: 'VEN', date: '25/09', time: '19:30' },
        ],
      },
      {
        id: 'trial-fo-caraibico', name: 'Caraibico', color: '#FF7043',
        slots: [
          { day: 'MER', date: '23/09', time: '20:30' },
          { day: 'MER', date: '23/09', time: '21:30' },
          { day: 'VEN', date: '25/09', time: '20:30' },
          { day: 'VEN', date: '25/09', time: '21:30' },
        ],
      },
    ],
  },
  abbadia: {
    label: 'Abbadia San Salvatore',
    venue: 'Palestra Athena',
    address: 'Via Arno 15, Abbadia San Salvatore',
    period: 'Martedì 29 Settembre 2026',
    disciplines: [
      {
        id: 'trial-ab-baby-dance', name: 'Baby Dance', ageNote: '4/6 anni', color: '#FBC02D',
        slots: [{ day: 'MAR', date: '29/09', time: '16:30' }],
      },
      {
        id: 'trial-ab-hip-hop-latino', name: 'Hip-Hop, Latino Americano', color: '#0288D1',
        slots: [
          { day: 'MAR', date: '29/09', time: '17:30', note: '6/10 anni' },
          { day: 'MAR', date: '29/09', time: '18:30', note: '11+ anni' },
        ],
      },
      {
        id: 'trial-ab-hip-hop-adulti', name: 'Hip-Hop Adulti', color: '#8E24AA',
        slots: [{ day: 'MAR', date: '29/09', time: '19:30' }],
      },
      {
        id: 'trial-ab-caraibico', name: 'Caraibico', color: '#FF7043',
        slots: [
          { day: 'MAR', date: '29/09', time: '20:30' },
          { day: 'MAR', date: '29/09', time: '21:30' },
        ],
      },
    ],
  },
};

const emptyForm = { student_name: '', student_age: '', parent_name: '', email: '', phone: '', notes: '' };

export default function TrialLessonsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sedeParam = searchParams.get('sede');
  const [locationKey, setLocationKey] = useState(LOCATIONS[sedeParam] ? sedeParam : 'grosseto');
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loc = LOCATIONS[locationKey];

  const openBooking = (discipline) => {
    setSelected(discipline);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        course_id: selected.id,
        student_name: form.student_name,
        student_age: form.student_age ? parseInt(form.student_age) : null,
        parent_name: form.parent_name || null,
        email: form.email,
        phone: form.phone,
        notes: form.notes || null,
      });
      toast.success('Richiesta inviata! Ti contatteremo per confermare la tua lezione di prova gratuita.');
      setDialogOpen(false);
      setForm(emptyForm);
    } catch (err) {
      toast.error('Errore nella richiesta. Riprova.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0288D1] via-[#0277BD] to-[#01579B]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full bg-[#FFCA28]" />
          <div className="absolute bottom-0 right-10 w-80 h-80 rounded-full bg-white" />
          <div className="absolute top-20 right-1/3 w-32 h-32 rounded-full bg-[#FF7043]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#FFCA28] text-slate-900 font-heading font-black text-sm uppercase tracking-widest rounded-full mb-6 animate-bounce" data-testid="trial-free-badge">
            <Gift size={16} /> Prima Prova Gratuita!
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-4">
            Lezioni di Prova <span className="text-[#A5F14B]">GRATUITE</span>!
          </h1>
          <p className="text-base md:text-lg text-blue-100 max-w-2xl mx-auto mb-6">
            Scopri la danza, balla Blue Moon! La prima lezione è sempre gratuita: scegli la tua sede, trova l'orario perfetto e prenota il tuo posto.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-md border border-white/25 rounded-full text-white font-heading font-bold" data-testid="trial-period-badge">
              <CalendarDays size={18} className="text-[#FFCA28]" />
              Dal 21 Settembre al 1 Ottobre 2026
            </div>
            <a
              href={`https://wa.me/?text=${encodeURIComponent('💃 Lezioni di Prova GRATUITE alla Caribbean Blue Moon Dance School! Dal 21 settembre al 1 ottobre a Grosseto, Follonica e Abbadia San Salvatore: la prima lezione è gratis! Scopri gli orari e prenota qui 👉 ' + window.location.origin + '/lezioni-prova')}`}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-share-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1EBE5A] text-white font-heading font-bold rounded-full shadow-lg transition-colors"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Condividi su WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Location tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 -mt-7 relative z-10">
        <div className="flex flex-col sm:flex-row gap-2 bg-white rounded-3xl sm:rounded-full p-2 shadow-xl shadow-slate-200/60 border border-slate-100 max-w-3xl mx-auto">
          {Object.entries(LOCATIONS).map(([key, l]) => (
            <button
              key={key}
              data-testid={`trial-tab-${key}`}
              onClick={() => { setLocationKey(key); setSearchParams({ sede: key }, { replace: true }); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full font-heading font-bold text-sm transition-colors ${
                locationKey === key
                  ? 'bg-[#FF7043] text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <MapPin size={15} /> {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10 md:py-12">
        {/* Venue info */}
        <div className="text-center mb-8" data-testid="trial-venue-info">
          <h2 className="font-heading text-lg md:text-lg font-black text-slate-900">
            Prove Gratuite di {loc.label}
          </h2>
          <p className="text-slate-500 mt-1 flex items-center justify-center gap-1.5 flex-wrap text-sm md:text-base">
            <MapPin size={16} className="text-[#FF7043]" />
            <span className="font-semibold">{loc.venue}</span> — {loc.address}
          </p>
          <p className="inline-flex items-center gap-1.5 mt-2 px-4 py-1.5 bg-[#0288D1]/10 text-[#0277BD] text-xs md:text-sm font-bold rounded-full">
            <CalendarDays size={14} /> {loc.period}
          </p>
        </div>

        {/* Disciplines grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loc.disciplines.map(d => (
            <div
              key={d.id}
              data-testid={`trial-card-${d.id}`}
              className="card-hover relative bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/60 overflow-hidden flex flex-col"
            >
              <div className="h-1.5 w-full" style={{ backgroundColor: d.color }} />
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-heading font-black text-xl text-slate-900 leading-tight">
                    {d.name}
                    {d.ageNote && <span className="block text-sm font-bold text-slate-400 mt-0.5">{d.ageNote}</span>}
                  </h3>
                  {d.isNew && (
                    <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 bg-[#A5F14B] text-slate-900 text-[10px] font-black uppercase tracking-wider rounded-full">
                      <Sparkles size={10} /> New
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-4 mb-5">
                  {d.slots.map((s, i) => (
                    <div
                      key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl text-xs"
                    >
                      <span className="font-black text-slate-900">{s.day} {s.date}</span>
                      <span className="inline-flex items-center gap-1 font-bold" style={{ color: d.color }}>
                        <Clock size={11} /> {s.time}
                      </span>
                      {s.note && <span className="text-slate-400 font-semibold">· {s.note}</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-auto">
                  <button
                    data-testid={`trial-book-${d.id}`}
                    onClick={() => openBooking(d)}
                    className="w-full inline-flex items-center justify-center gap-1 px-5 py-3 bg-[#FF7043] text-white font-heading font-bold text-sm rounded-full btn-caribbean"
                  >
                    Prenota la Prova Gratuita <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Location bar */}
        <div className="mt-12 bg-[#0288D1] rounded-3xl px-6 py-6 md:px-10 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10 text-white text-center" data-testid="trial-location-bar">
          <div className="flex items-center gap-2 font-heading font-bold">
            <MapPin size={18} className="text-[#FFCA28]" />
            {loc.venue} — {loc.address}
          </div>
          <a href="tel:3801599564" className="flex items-center gap-2 font-heading font-bold hover:text-[#FFCA28] transition-colors">
            <Phone size={18} className="text-[#FFCA28]" /> 380 159 9564
          </a>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="trial-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Prenota la Lezione di Prova Gratuita</DialogTitle>
            <DialogDescription>
              {selected && <span className="text-[#FF7043] font-semibold">{selected.name} — {loc.label}</span>}
              {' '}({loc.venue}, {loc.address}). La prima lezione è gratuita! Lascia i tuoi dati e ti confermeremo il posto.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="flex flex-wrap gap-1.5 -mt-1">
              {selected.slots.map((s, i) => (
                <span key={i} className="px-2.5 py-1 bg-[#FFF8F5] border border-[#FF7043]/20 rounded-lg text-[11px] font-bold text-slate-700">
                  {s.day} {s.date} · {s.time}{s.note ? ` (${s.note})` : ''}
                </span>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="trial_student_name">Nome Studente *</Label>
                <Input
                  id="trial_student_name"
                  data-testid="trial-booking-student-name"
                  required
                  value={form.student_name}
                  onChange={e => setForm(f => ({ ...f, student_name: e.target.value }))}
                  placeholder="Mario Rossi"
                />
              </div>
              <div>
                <Label htmlFor="trial_student_age">Età</Label>
                <Input
                  id="trial_student_age"
                  data-testid="trial-booking-student-age"
                  type="number"
                  min="3"
                  max="99"
                  value={form.student_age}
                  onChange={e => setForm(f => ({ ...f, student_age: e.target.value }))}
                  placeholder="12"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="trial_parent_name">Nome Genitore (se minorenne)</Label>
              <Input
                id="trial_parent_name"
                data-testid="trial-booking-parent-name"
                value={form.parent_name}
                onChange={e => setForm(f => ({ ...f, parent_name: e.target.value }))}
                placeholder="Nome del genitore"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="trial_email">Email *</Label>
                <Input
                  id="trial_email"
                  data-testid="trial-booking-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@esempio.it"
                />
              </div>
              <div>
                <Label htmlFor="trial_phone">Telefono *</Label>
                <Input
                  id="trial_phone"
                  data-testid="trial-booking-phone"
                  required
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="380 123 4567"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="trial_notes">Giorno/orario preferito e note</Label>
              <Textarea
                id="trial_notes"
                data-testid="trial-booking-notes"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Es. Preferisco MER 23/09 ore 19:30..."
                rows={3}
              />
            </div>

            <div className="flex items-start gap-2">
              <input type="checkbox" id="trial-privacy-consent" required className="mt-1 accent-[#FF7043]" data-testid="trial-booking-privacy-consent" />
              <label htmlFor="trial-privacy-consent" className="text-xs text-slate-500">
                Accetto il trattamento dei miei dati personali in conformità alla <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#0288D1] underline">Privacy Policy</a> per la gestione della mia richiesta. *
              </label>
            </div>

            <button
              type="submit"
              data-testid="trial-booking-submit-btn"
              disabled={loading}
              className="w-full py-3.5 bg-[#FF7043] text-white font-heading font-bold text-base rounded-full btn-caribbean disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Invio in corso...' : 'Prenota la Prova Gratuita'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
